import React, { type ReactNode } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import Fallback from '../fallback';
import { isStaleChunkError, attemptStaleChunkReload } from '@/utils/stale-chunk-reload';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Custom fallback. If given, it replaces the default `<Fallback error onReset />`. */
  fallback?: ReactNode;
  /**
   * When any value here changes while the boundary is showing the fallback, it resets and
   * re-renders `children`. Pass `[location.pathname]` so navigating away from a crashed screen
   * automatically recovers.
   */
  resetKeys?: ReadonlyArray<unknown>;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

const keysChanged = (a?: ReadonlyArray<unknown>, b?: ReadonlyArray<unknown>): boolean => {
  if (a === b) return false;
  if (!a || !b || a.length !== b.length) return true;
  return a.some((value, index) => !Object.is(value, b[index]));
};

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidMount() {
    window.addEventListener('online', this.handleOnline);
  }

  componentWillUnmount() {
    window.removeEventListener('online', this.handleOnline);
  }

  // Coming back online recovers crashes caused by a failed offline chunk load.
  handleOnline = () => {
    if (this.state.hasError) {
      this.reset();
    }
  };

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    if (isStaleChunkError(error)) {
      // Online: most likely a deployed-over bundle — a guarded (capped) reload recovers it.
      if (attemptStaleChunkReload()) return;
      // Offline: not a real crash — the fallback shows an offline notice and `handleOnline` recovers it.
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
    }

    datadogRum.addError(error, {
      source: 'react-error-boundary',
      componentStack: errorInfo.componentStack,
    });
    this.props.onError?.(error, errorInfo);
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    if (this.state.hasError && keysChanged(prevProps.resetKeys, this.props.resetKeys)) {
      this.reset();
    }
  }

  reset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return this.props.fallback ?? <Fallback error={this.state.error} onReset={this.reset} />;
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
